import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { breachApi, organizationApi } from '../services/api';
import {
  DataBreachCreate,
  DataBreachUpdate,
  BreachCategory,
  BreachSeverity,
  BreachTemplate
} from '@dsgvo-buddy/types';
import {
  Title,
  Text,
  Button,
  Input,
  TextArea,
  Select,
  Option,
  ProgressIndicator,
  FlexBox,
  Card,
  Label,
  Icon,
  MessageStrip,
  BusyIndicator,
  DatePicker
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import HelpTooltip from '../components/HelpTooltip';
import { fieldHelp } from '../helpers/helpTexts';
import { isoToGerman, germanToIso } from '../helpers/dateFormat';

interface BreachWizardPageProps {
  editMode?: boolean;
}

const categoryConfig: Record<BreachCategory, { label: string; description: string }> = {
  [BreachCategory.UNAUTHORIZED_ACCESS]: { label: 'Unbefugter Zugriff', description: 'Unbefugter Zugriff auf personenbezogene Daten' },
  [BreachCategory.DATA_LOSS]: { label: 'Datenverlust', description: 'Verlust von personenbezogenen Daten' },
  [BreachCategory.UNAUTHORIZED_DISCLOSURE]: { label: 'Unbefugte Weitergabe', description: 'Weitergabe an nicht autorisierte Personen' },
  [BreachCategory.ACCIDENTAL_DELETION]: { label: 'Versehentliche Löschung', description: 'Versehentliche Löschung von Daten' },
  [BreachCategory.DEVICE_LOSS]: { label: 'Geräteverlust', description: 'Verlust eines Gerätes mit personenbezogenen Daten' },
  [BreachCategory.RANSOMWARE]: { label: 'Ransomware', description: 'Ransomware-Angriff mit Datenverschlüsselung' },
  [BreachCategory.MISCONFIGURATION]: { label: 'Fehlkonfiguration', description: 'Fehlkonfiguration von Systemen oder Software' },
  [BreachCategory.SOCIAL_ENGINEERING]: { label: 'Social Engineering', description: 'Phishing, Pretexting oder andere Social-Engineering-Angriffe' },
  [BreachCategory.OTHER]: { label: 'Sonstiges', description: 'Sonstiger Datenschutzvorfall' }
};

const severityConfig: Record<BreachSeverity, { label: string }> = {
  [BreachSeverity.LOW]: { label: 'Gering' },
  [BreachSeverity.MEDIUM]: { label: 'Mittel' },
  [BreachSeverity.HIGH]: { label: 'Hoch' },
  [BreachSeverity.CRITICAL]: { label: 'Kritisch' }
};

const steps = [
  { title: 'Grunddaten', icon: 'hint' },
  { title: 'Beschreibung', icon: 'detail-more' },
  { title: 'Sofortmaßnahmen', icon: 'shield' }
];

interface FormData {
  organization_id: number | null;
  title: string;
  category: BreachCategory;
  severity: BreachSeverity;
  incident_at: string;
  detected_at: string;
  description: string;
  affected_data: string;
  affected_count: string;
  cause: string;
  immediate_measures: string;
  assigned_to: string;
}

const defaultFormData: FormData = {
  organization_id: null,
  title: '',
  category: BreachCategory.UNAUTHORIZED_ACCESS,
  severity: BreachSeverity.MEDIUM,
  incident_at: '',
  detected_at: new Date().toISOString().slice(0, 10),
  description: '',
  affected_data: '',
  affected_count: '',
  cause: '',
  immediate_measures: '',
  assigned_to: ''
};

export default function BreachWizardPage({ editMode = false }: BreachWizardPageProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(editMode);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [templates, setTemplates] = useState<BreachTemplate[]>([]);

  useEffect(() => { loadInitialData(); }, []);

  const loadInitialData = async () => {
    const [orgsRes, templatesRes] = await Promise.all([
      organizationApi.getAll(),
      breachApi.getTemplates()
    ]);

    if (orgsRes.success && orgsRes.data) {
      if (orgsRes.data.length > 0 && !editMode) {
        setFormData(prev => ({ ...prev, organization_id: orgsRes.data![0].id }));
      }
    }

    if (templatesRes.success && templatesRes.data) {
      setTemplates(templatesRes.data);
    }

    if (editMode && id) {
      const org = orgsRes.data?.[0];
      if (org) {
        const res = await breachApi.getById(parseInt(id), org.id);
        if (res.success && res.data) {
          const d = res.data;
          setFormData({
            organization_id: d.organization_id,
            title: d.title,
            category: d.category,
            severity: d.severity,
            incident_at: d.incident_at.slice(0, 10),
            detected_at: d.detected_at.slice(0, 10),
            description: d.description || '',
            affected_data: d.affected_data || '',
            affected_count: d.affected_count != null ? String(d.affected_count) : '',
            cause: d.cause || '',
            immediate_measures: d.immediate_measures || '',
            assigned_to: d.assigned_to != null ? String(d.assigned_to) : ''
          });
        } else {
          navigate('/breaches');
        }
      }
    }

    setInitialLoading(false);
  };

  const applyTemplate = (template: BreachTemplate) => {
    setFormData(prev => ({
      ...prev,
      title: template.title,
      category: template.category,
      severity: template.severity,
      description: template.description,
      affected_data: template.affected_data,
      immediate_measures: template.immediate_measures
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    if (editMode && id) {
      const updates: DataBreachUpdate = {
        title: formData.title,
        category: formData.category,
        severity: formData.severity,
        incident_at: formData.incident_at,
        detected_at: formData.detected_at,
        description: formData.description || undefined,
        affected_data: formData.affected_data || undefined,
        affected_count: formData.affected_count ? parseInt(formData.affected_count) : undefined,
        cause: formData.cause || undefined,
        immediate_measures: formData.immediate_measures || undefined,
        assigned_to: formData.assigned_to ? parseInt(formData.assigned_to) : undefined
      };
      const response = await breachApi.update(parseInt(id), formData.organization_id!, updates);
      if (response.success) {
        navigate(`/breaches/${id}`);
      } else {
        setError(response.error || 'Fehler beim Aktualisieren');
      }
    } else {
      const data: DataBreachCreate = {
        organization_id: formData.organization_id!,
        title: formData.title,
        category: formData.category,
        severity: formData.severity,
        incident_at: formData.incident_at,
        detected_at: formData.detected_at,
        description: formData.description || undefined,
        affected_data: formData.affected_data || undefined,
        affected_count: formData.affected_count ? parseInt(formData.affected_count) : undefined,
        cause: formData.cause || undefined,
        assigned_to: formData.assigned_to ? parseInt(formData.assigned_to) : undefined
      };
      const response = await breachApi.create(data);
      if (response.success && response.data) {
        navigate(`/breaches/${response.data.id}`);
      } else {
        setError(response.error || 'Fehler beim Erstellen');
      }
    }

    setLoading(false);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return formData.title.trim() !== '' && formData.incident_at.trim() !== '' && formData.detected_at.trim() !== '';
      case 1: return true;
      case 2: return true;
      default: return false;
    }
  };

  if (initialLoading) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  return (
    <div className="page-layout--narrow">
      {/* Header */}
      <FlexBox justifyContent="SpaceBetween" alignItems="Center" style={{ marginBottom: '1.5rem' }}>
        <div>
          <Title level="H3" style={{ marginBottom: '0.25rem' }}>
            {editMode ? 'Vorfall bearbeiten' : 'Neuen Datenschutzvorfall melden'}
          </Title>
          <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
            Meldung gemäß Art. 33/34 DSGVO
          </Text>
        </div>
        <Button icon="decline" design="Transparent" onClick={() => navigate('/breaches')}>
          Abbrechen
        </Button>
      </FlexBox>

      {/* Progress */}
      <FlexBox style={{ gap: '0.5rem', marginBottom: '1.5rem' }}>
        {steps.map((step, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              padding: '0.75rem',
              borderRadius: '0.5rem',
              background: i === currentStep
                ? 'var(--sapSelectedColor, #0070f2)'
                : i < currentStep
                  ? 'var(--sapPositiveColor, #107e3e)'
                  : 'var(--sapNeutralBackground, #f5f6f7)',
              color: i <= currentStep ? 'white' : 'var(--sapNeutralTextColor)',
              textAlign: 'center',
              cursor: i < currentStep ? 'pointer' : 'default',
              transition: 'background 0.2s'
            }}
            onClick={() => { if (i < currentStep) setCurrentStep(i); }}
          >
            <Icon name={step.icon} style={{ marginRight: '0.5rem' }} />
            <Text style={{ fontWeight: '600', color: 'inherit', fontSize: '0.8125rem' }}>
              {i + 1}. {step.title}
            </Text>
          </div>
        ))}
      </FlexBox>

      <ProgressIndicator
        value={((currentStep + 1) / steps.length) * 100}
        style={{ marginBottom: '1.5rem' }}
      />

      {error && (
        <MessageStrip design="Negative" style={{ marginBottom: '1rem' }}>
          {error}
        </MessageStrip>
      )}

      {/* Step Content */}
      <Card style={{ marginBottom: '1.5rem' }}>
        <div style={{ padding: '1.5rem' }}>
          {currentStep === 0 && (
            <div>
              <Title level="H5" style={{ marginBottom: '1rem' }}>Grunddaten des Vorfalls</Title>

              {/* Templates */}
              {templates.length > 0 && !editMode && (
                <div style={{ marginBottom: '1.25rem' }}>
                  <Label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Vorlage verwenden</Label>
                  <FlexBox wrap="Wrap" style={{ gap: '0.5rem' }}>
                    {templates.map((t, i) => (
                      <Button key={i} design="Default" onClick={() => applyTemplate(t)} style={{ fontSize: '0.8125rem' }}>
                        {t.title}
                      </Button>
                    ))}
                  </FlexBox>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                {/* Title */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <Label required>Titel des Vorfalls</Label>
                    <HelpTooltip text={fieldHelp.breach.title} inline />
                  </div>
                  <Input
                    value={formData.title}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="z.B. Unbefugter Zugriff auf Kundendatenbank"
                    style={{ width: '100%' }}
                  />
                </div>

                {/* Category */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <Label required style={{ fontWeight: '600' }}>Kategorie</Label>
                    <HelpTooltip text={fieldHelp.breach.category} inline />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.5rem' }}>
                    {Object.entries(categoryConfig).map(([key, cfg]) => (
                      <div
                        key={key}
                        style={{
                          padding: '0.75rem',
                          borderRadius: '0.375rem',
                          border: formData.category === key
                            ? '2px solid var(--sapSelectedColor, #0070f2)'
                            : '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)',
                          background: formData.category === key ? 'var(--sapSelectedBackground, #e8f0fe)' : 'transparent',
                          cursor: 'pointer'
                        }}
                        onClick={() => setFormData(prev => ({ ...prev, category: key as BreachCategory }))}
                      >
                        <Text style={{ fontWeight: '600', fontSize: '0.875rem' }}>{cfg.label}</Text>
                        <Text style={{ display: 'block', fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', marginTop: '0.125rem' }}>
                          {cfg.description}
                        </Text>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Severity */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <Label>Schweregrad</Label>
                    <HelpTooltip text={fieldHelp.breach.severity} inline />
                  </div>
                  <Select
                    onChange={(e: any) => setFormData(prev => ({ ...prev, severity: e.detail.selectedOption.value as BreachSeverity }))}
                    style={{ width: '100%', maxWidth: '400px' }}
                  >
                    {Object.entries(severityConfig).map(([key, cfg]) => (
                      <Option key={key} value={key} selected={formData.severity === key}>{cfg.label}</Option>
                    ))}
                  </Select>
                </div>

                {/* Incident At */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <Label required>Zeitpunkt des Vorfalls</Label>
                    <HelpTooltip text={fieldHelp.breach.incident_at} inline />
                  </div>
                  <DatePicker
                    formatPattern="dd.MM.yyyy"
                    value={isoToGerman(formData.incident_at)}
                    onChange={(e: any) => setFormData(prev => ({ ...prev, incident_at: germanToIso(e.detail.value) }))}
                    style={{ width: '100%', maxWidth: '300px' }}
                  />
                </div>

                {/* Detected At */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <Label required>Zeitpunkt der Entdeckung</Label>
                    <HelpTooltip text={fieldHelp.breach.detected_at} inline />
                  </div>
                  <DatePicker
                    formatPattern="dd.MM.yyyy"
                    value={isoToGerman(formData.detected_at)}
                    onChange={(e: any) => setFormData(prev => ({ ...prev, detected_at: germanToIso(e.detail.value) }))}
                    style={{ width: '100%', maxWidth: '300px' }}
                  />
                </div>
              </div>
            </div>
          )}

          {currentStep === 1 && (
            <div>
              <Title level="H5" style={{ marginBottom: '1rem' }}>Beschreibung des Vorfalls</Title>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Description */}
                <div>
                  <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Was ist passiert?</Label>
                  <TextArea
                    value={formData.description}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Beschreiben Sie den Vorfall so detailliert wie möglich..."
                    rows={4}
                    style={{ width: '100%' }}
                  />
                </div>

                {/* Affected Data */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <Label>Betroffene Daten</Label>
                    <HelpTooltip text={fieldHelp.breach.affected_data} inline />
                  </div>
                  <TextArea
                    value={formData.affected_data}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, affected_data: e.target.value }))}
                    placeholder="Welche Arten personenbezogener Daten sind betroffen?"
                    rows={3}
                    style={{ width: '100%' }}
                  />
                </div>

                {/* Affected Count */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <Label>Geschätzte Anzahl Betroffener</Label>
                    <HelpTooltip text={fieldHelp.breach.affected_count} inline />
                  </div>
                  <Input
                    value={formData.affected_count}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, affected_count: e.target.value }))}
                    placeholder="z.B. 500"
                    style={{ width: '100%', maxWidth: '300px' }}
                  />
                </div>

                {/* Cause */}
                <div>
                  <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Ursache</Label>
                  <TextArea
                    value={formData.cause}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, cause: e.target.value }))}
                    placeholder="Was hat den Vorfall verursacht?"
                    rows={3}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div>
              <Title level="H5" style={{ marginBottom: '1rem' }}>Sofortmaßnahmen</Title>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Immediate Measures */}
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <Label>Ergriffene Sofortmaßnahmen</Label>
                    <HelpTooltip text={fieldHelp.breach.immediate_measures} inline />
                  </div>
                  <TextArea
                    value={formData.immediate_measures}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, immediate_measures: e.target.value }))}
                    placeholder="Welche Sofortmaßnahmen wurden ergriffen?"
                    rows={5}
                    style={{ width: '100%' }}
                  />
                </div>

                {/* Assigned To */}
                <div>
                  <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Zugewiesen an (Benutzer-ID)</Label>
                  <Input
                    value={formData.assigned_to}
                    onInput={(e: any) => setFormData(prev => ({ ...prev, assigned_to: e.target.value }))}
                    placeholder="Benutzer-ID (optional)"
                    style={{ width: '100%', maxWidth: '300px' }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Navigation Buttons */}
      <FlexBox justifyContent="SpaceBetween">
        <Button
          icon="nav-back"
          design="Transparent"
          onClick={() => {
            if (currentStep > 0) setCurrentStep(currentStep - 1);
            else navigate('/breaches');
          }}
        >
          {currentStep > 0 ? 'Zurück' : 'Abbrechen'}
        </Button>

        {currentStep < steps.length - 1 ? (
          <Button
            icon="navigation-right-arrow"
            iconEnd
            design="Emphasized"
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={!canProceed()}
          >
            Weiter
          </Button>
        ) : (
          <Button
            icon="save"
            design="Emphasized"
            onClick={handleSubmit}
            disabled={loading || !formData.title.trim() || !formData.incident_at.trim() || !formData.detected_at.trim()}
          >
            {loading ? 'Wird gespeichert...' : editMode ? 'Aktualisieren' : 'Vorfall melden'}
          </Button>
        )}
      </FlexBox>
    </div>
  );
}
